import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../services/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Users, Activity, MessageSquare, Clock, RefreshCw, ChevronRight, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const relTime = (iso) => {
  if (!iso) return 'Never';
  try {
    const then = new Date(iso);
    const diff = Date.now() - then.getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'Just now';
    if (mins < 60) return `${mins} min ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs} h ago`;
    const days = Math.floor(hrs / 24);
    if (days < 30) return `${days} d ago`;
    return then.toLocaleDateString();
  } catch { return '—'; }
};

const initials = (nameOrEmail) => {
  if (!nameOrEmail) return 'M';
  const src = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] : nameOrEmail;
  return src.split(/[\s._-]+/).filter(Boolean).slice(0, 2).map(s => s[0]?.toUpperCase() || '').join('') || 'M';
};

export default function ModeratorPerformancePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);

  const load = async (windowDays = days) => {
    setLoading(true);
    try {
      const res = await api.get(`/moderators/performance?days=${windowDays}`);
      setData(res.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to load moderator performance');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(days); /* eslint-disable-line react-hooks/exhaustive-deps */ }, [days]);

  const agg = data?.aggregate || { total_messages: 0, total_moderators: 0, active_moderators: 0 };
  const rows = data?.moderators || [];

  return (
    <div className="max-w-7xl mx-auto p-6 space-y-6" data-testid="moderator-performance-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <TrendingUp className="w-6 h-6 text-blue-600" />
            Moderator Performance
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Track how your assigned moderators are handling conversations across every AI Employee you own.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Select value={String(days)} onValueChange={(v) => setDays(parseInt(v, 10))}>
            <SelectTrigger className="w-[160px]" data-testid="moderator-perf-timeframe">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Last 7 days</SelectItem>
              <SelectItem value="30">Last 30 days</SelectItem>
              <SelectItem value="90">Last 90 days</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => load(days)} data-testid="moderator-perf-refresh">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Aggregate stats */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <Card data-testid="stat-total-moderators">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Moderators</div>
              <div className="text-2xl font-semibold text-slate-900">{agg.total_moderators}</div>
              <div className="text-xs text-emerald-600">{agg.active_moderators} active</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-total-messages">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <MessageSquare className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Messages handled</div>
              <div className="text-2xl font-semibold text-slate-900">{agg.total_messages.toLocaleString()}</div>
              <div className="text-xs text-slate-500">in last {days} days</div>
            </div>
          </CardContent>
        </Card>
        <Card data-testid="stat-avg-per-mod">
          <CardContent className="p-5 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-indigo-50 flex items-center justify-center">
              <Activity className="w-5 h-5 text-indigo-600" />
            </div>
            <div>
              <div className="text-xs text-slate-500 uppercase tracking-wide">Avg per moderator</div>
              <div className="text-2xl font-semibold text-slate-900">
                {agg.total_moderators ? Math.round(agg.total_messages / agg.total_moderators).toLocaleString() : 0}
              </div>
              <div className="text-xs text-slate-500">messages</div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Moderator list */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base text-slate-900">Moderator activity</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center text-slate-500 text-sm">Loading…</div>
          ) : rows.length === 0 ? (
            <div className="p-10 text-center text-slate-500 text-sm" data-testid="moderator-perf-empty">
              <Users className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="font-medium text-slate-700 mb-1">No moderators yet</div>
              <div className="text-xs">Add moderators from any AI Employee&apos;s Moderators tab to start tracking their activity here.</div>
            </div>
          ) : (
            <div className="divide-y divide-slate-100">
              {rows.map((m) => (
                <div
                  key={m.user_id}
                  className="p-5 hover:bg-slate-50 transition-colors"
                  data-testid={`moderator-perf-row-${m.user_id}`}
                >
                  <div className="flex items-start justify-between gap-4 flex-wrap">
                    <div className="flex items-start gap-3 min-w-0 flex-1">
                      <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white text-sm font-semibold flex items-center justify-center shrink-0">
                        {initials(m.name || m.email)}
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <div className="font-medium text-slate-900 truncate">{m.name || m.email}</div>
                          {m.status === 'active' ? (
                            <Badge variant="outline" className="text-emerald-700 border-emerald-200 bg-emerald-50">Active</Badge>
                          ) : (
                            <Badge variant="outline" className="text-slate-600 border-slate-200 bg-slate-50">Suspended</Badge>
                          )}
                        </div>
                        {m.name && <div className="text-xs text-slate-500 truncate">{m.email}</div>}
                        <div className="flex flex-wrap items-center gap-1 mt-2">
                          {m.chatbots?.map((c) => (
                            <Link
                              key={`${m.user_id}-${c.chatbot_id}`}
                              to={`/dashboard/chatbots/${c.chatbot_id}`}
                              className="text-xs text-blue-600 hover:text-blue-800 hover:underline bg-blue-50 px-2 py-0.5 rounded"
                              data-testid={`moderator-perf-bot-${m.user_id}-${c.chatbot_id}`}
                            >
                              {c.chatbot_name}
                            </Link>
                          ))}
                        </div>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 shrink-0">
                      <div>
                        <div className="text-xs text-slate-500">Messages</div>
                        <div className="text-lg font-semibold text-slate-900" data-testid={`metric-messages-${m.user_id}`}>
                          {m.messages_handled.toLocaleString()}
                        </div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Last 7d</div>
                        <div className="text-lg font-semibold text-slate-900">{m.messages_last_7d.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500">Threads</div>
                        <div className="text-lg font-semibold text-slate-900">{m.conversations_handled.toLocaleString()}</div>
                      </div>
                      <div>
                        <div className="text-xs text-slate-500 flex items-center gap-1"><Clock className="w-3 h-3" />Last active</div>
                        <div className="text-sm font-medium text-slate-700">{relTime(m.last_active_at)}</div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <div className="text-xs text-slate-400 flex items-center gap-1">
        <ChevronRight className="w-3 h-3" />
        Metrics cover only human replies (both website widget and social channels). AI-generated responses are excluded.
      </div>
    </div>
  );
}
